import { useEffect, useState } from 'react';

import type { SessionData } from '../../../../public-src/core/js/schibsted-account/types.js';

import handleLocalStorage from '../components/newsletter/handleLocalStorage.js';
import { newsletterByfunnApi as newsletterApi } from '../../../../public-src/aftenposten/js/newsletter-api.js';

interface NewsletterData {
  newsletterId: string;
  newsletterPublication: string;
  session?: SessionData;
}

const useIsUserAlreadySubscribed = ({
  newsletterId,
  newsletterPublication,
  session,
}: NewsletterData) => {
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState<boolean>(true);

  const userId = session ? session.user.userId : null;

  useEffect(() => {
    const isUserSubscribedToNewsletter = async (): Promise<boolean> => {
      if (!userId) return false;

      const LS = handleLocalStorage({
        newsletterId,
        newsletterPublication,
        userId,
      });

      if (LS.userJustLoggedInViaNewsletterWidget()) return false;

      if (LS.userHasValidLocalStorage(LS.getSubscribedData())) return true;

      if (LS.userHasValidLocalStorage(LS.getNotSubscribedData())) return false;

      // Check via API if none of the localstorages above was valid.
      const userIsAlreadySubscribed =
        await newsletterApi.getSubscriptionStatus(newsletterId);

      if (userIsAlreadySubscribed) {
        LS.setSubscribedData();
        LS.deleteNotSubscribedData();
      } else {
        LS.setNotSubscribedData();
        LS.deleteSubscribedData();
      }

      return userIsAlreadySubscribed.subscribed;
    };

    const setInitialSubscribedValue = async () => {
      setIsAlreadySubscribed(await isUserSubscribedToNewsletter());
    };

    setInitialSubscribedValue().catch(() => setIsAlreadySubscribed(false));
  }, [newsletterId, userId, newsletterPublication]);

  return { isAlreadySubscribed };
};

export default useIsUserAlreadySubscribed;
